// FICHA COMERCIAL 360 del cliente (ronda 1): UNA sola ficha, se llegue desde Ctrl+K, desde el
// directorio de Clientes o desde un enlace. Cliente · comercial · direcciones · contactos, y las
// pestañas Cartola, Producto, Pedidos y Últimas compras. «Editar / solicitar cambio» (Alt+E) aplica
// lo operativo directo y deja lo comercial sensible PENDIENTE (el valor efectivo no cambia hasta que
// un supervisor lo apruebe). «Nueva NV para este cliente» (Alt+N) abre la nota con el cliente puesto.
import { useMemo, useState } from 'react';
import {
  useContextoComercial,
  useFichaCliente,
  useListasPrecio,
  usePoliticaFicha,
  useSolicitudesCambio,
  useVendedores,
} from '../api/consultas';
import type { CampoFicha, ResultadoCambios, SolicitudCambio } from '../api/contratos';
import { PanelError } from '../comun/Estados';
import { NOMBRE_CONDICION, pesos, rut } from '../comun/formato';
import { useSesion } from '../sesion/SesionContexto';
import { useAtajos } from '../shell/atajos';
import { direccionPorDefecto } from '../ventas/comercial';
import { PanelContexto, type PestanaContexto } from '../ventas/PanelContexto';
import { DecidirSolicitud, useNombresFicha } from './BandejaSolicitudes';
import { EditarFicha } from './EditarFicha';
import { FichaMaestro } from './FichaMaestro';
import { NOMBRE_CAMPO, textoValor } from './valoresFicha';

export function ContextoCliente({
  activa,
  entidadId,
  alAbrirNota,
  alNuevaNota,
}: {
  activa: boolean;
  entidadId: string;
  alAbrirNota: (notaId: string, numero: string) => void;
  alNuevaNota: (c: { id: string; razonSocial: string }) => void;
}) {
  const { empresa, puede, usuario } = useSesion();
  const empresaId = empresa!.id;
  const ficha = useFichaCliente(empresaId, entidadId);
  const vendedores = useVendedores(empresaId);
  const listas = useListasPrecio(empresaId);
  // Las zonas de cada dirección las deriva el backend (contexto comercial).
  const contexto = useContextoComercial(empresaId, entidadId, 'VENDEDOR');
  const politica = usePoliticaFicha(empresaId, entidadId);
  const solicitudes = useSolicitudesCambio(empresaId, { entidadId, estado: 'PENDIENTE' });
  const nombres = useNombresFicha(empresaId);
  const [pestana, setPestana] = useState<PestanaContexto>('cliente');
  /** «Resumen» (la ficha 360 de consulta) o «Ficha» (la mantención completa, como Random). */
  // La ficha completa primero (Rodrigo, 2026-10-24); el resumen 360 queda a un clic.
  const [vista, setVista] = useState<'resumen' | 'ficha'>('ficha');
  const [editando, setEditando] = useState(false);
  const [resultado, setResultado] = useState<ResultadoCambios | null>(null);
  const [cancelar, setCancelar] = useState<SolicitudCambio | null>(null);
  const puedeEditar = !!politica.data?.some((p) => p.aplicacion !== 'NO_DISPONIBLE');

  const pendientes = useMemo(() => {
    const m = new Map<CampoFicha, string>();
    for (const s of solicitudes.data ?? [])
      for (const c of s.campos) m.set(c.campo, textoValor(c.campo, c.valor_solicitado, nombres));
    return m;
  }, [solicitudes.data, nombres.vendedor, nombres.lista]);

  const f = ficha.data;
  const nueva = () => f && alNuevaNota({ id: f.entidad.id, razonSocial: f.entidad.razon_social });
  useAtajos(activa && !editando && !cancelar, {
    F5: () => setPestana('cartola'),
    F6: () => setPestana('pedidos'),
    F7: () => setPestana('compras'),
    'Alt+E': () => puedeEditar && setEditando(true),
  });
  if (ficha.isPending) return <p className="apagado pantalla">Cargando cliente…</p>;
  if (ficha.isError) return <PanelError error={ficha.error} />;
  const ff = ficha.data;
  const rel = ff.empresas.find((e) => e.empresa_id === empresaId) ?? null;
  const vendedor = vendedores.data?.find((v) => v.id === rel?.vendedor_id) ?? null;
  const lista = listas.data?.find((l) => l.id === rel?.lista_precio_id) ?? null;
  return (
    <div
      className="pantalla contexto-cliente"
      data-pantalla="contexto"
      data-cliente={ff.entidad.id}
      data-ficha={ff.entidad.id}
    >
      <div className="barra-contexto">
        <span className="mono apagado">{rut(ff.entidad.rut, ff.entidad.dv)}</span>
        <h2>{ff.entidad.razon_social}</h2>
        {rel && (
          <span className="insignia tono-info">
            {NOMBRE_CONDICION[rel.condicion_pago] ?? rel.condicion_pago}
            {rel.plazo_dias ? ` ${rel.plazo_dias} días` : ''}
          </span>
        )}
        {rel?.linea_credito && (
          <span className="insignia tono-neutro">Línea {pesos(rel.linea_credito)}</span>
        )}
        {rel?.bloqueado && <span className="insignia tono-malo">BLOQUEADO</span>}
        {(solicitudes.data?.length ?? 0) > 0 && (
          <span className="insignia tono-aviso" data-cambios-pendientes>
            {solicitudes.data!.length} solicitud(es) de cambio pendiente(s)
          </span>
        )}
        <span className="espaciador" />
        {puedeEditar && (
          <button
            type="button"
            onClick={() => setEditando(true)}
            title="Editar lo operativo o solicitar un cambio comercial (Alt+E)"
          >
            <kbd>Alt+E</kbd> Editar / solicitar cambio
          </button>
        )}
        {puede('ventas.nota.crear') && (
          <button
            type="button"
            className="primario"
            onClick={nueva}
            title="Nueva NV para este cliente (Alt+N)"
          >
            <kbd>Alt+N</kbd> Nueva NV para este cliente
          </button>
        )}
      </div>
      {resultado && (
        <div className="aviso-linea tono-ok" role="status" data-resultado-cambios>
          {resultado.aplicados.length > 0 && (
            <span>Aplicado: {resultado.aplicados.map((c) => NOMBRE_CAMPO[c]).join(', ')}. </span>
          )}
          {resultado.pendientes.length > 0 && (
            <span>
              Pendiente de aprobación: {resultado.pendientes.map((c) => NOMBRE_CAMPO[c]).join(', ')}{' '}
              (el valor actual no cambia hasta que un supervisor lo apruebe).
            </span>
          )}
          <button type="button" className="enlace" onClick={() => setResultado(null)}>
            cerrar
          </button>
        </div>
      )}
      {(solicitudes.data ?? []).map((s) => (
        <div key={s.id} className="aviso-linea tono-aviso" data-solicitud-pendiente={s.id}>
          <b>CAMBIO PENDIENTE</b>{' '}
          {s.campos
            .map(
              (c) =>
                `${NOMBRE_CAMPO[c.campo]}: ${textoValor(c.campo, c.valor_actual, nombres)} → ${textoValor(c.campo, c.valor_solicitado, nombres)}`,
            )
            .join(' · ')}
          <span className="apagado"> · {s.motivo}</span>
          {s.solicitado_por === usuario?.id && (
            <button type="button" className="enlace" onClick={() => setCancelar(s)}>
              Cancelar mi solicitud
            </button>
          )}
        </div>
      ))}
      <div className="segmentos vista-cliente" role="radiogroup" aria-label="Vista del cliente">
        {(
          [
            ['ficha', 'Ficha del cliente'],
            ['resumen', 'Resumen 360'],
          ] as const
        ).map(([id, n]) => (
          <button
            key={id}
            type="button"
            role="radio"
            aria-checked={vista === id}
            className={vista === id ? 'activo' : ''}
            onClick={() => setVista(id)}
          >
            {n}
          </button>
        ))}
      </div>
      {vista === 'ficha' ? (
        <FichaMaestro
          key={`${ff.entidad.id}-${rel?.version ?? 0}`}
          empresaId={empresaId}
          ficha={ff}
          vendedores={vendedores.data ?? []}
          listas={listas.data ?? []}
          alSolicitarCambio={puedeEditar ? () => setEditando(true) : undefined}
        />
      ) : (
        <PanelContexto
          completo
          empresaId={empresaId}
          clienteId={ff.entidad.id}
          ficha={ff}
          vendedorNombre={vendedor ? `${vendedor.codigo} · ${vendedor.nombre}` : null}
          listaNombre={lista ? `${lista.codigo} ${lista.nombre}` : null}
          listaId={rel?.lista_precio_id ?? null}
          ubicacionId={null}
          direccion={direccionPorDefecto(ff.direcciones)}
          zonaDe={(id) => contexto.data?.direcciones.find((d) => d.id === id)?.zona?.nombre ?? null}
          pendientes={pendientes}
          pestana={pestana}
          alCambiarPestana={setPestana}
          linea={null}
          alAbrirNota={alAbrirNota}
        />
      )}
      {editando && (
        <EditarFicha
          empresaId={empresaId}
          ficha={ff}
          vendedores={vendedores.data ?? []}
          listas={listas.data ?? []}
          pendientes={new Set(pendientes.keys())}
          alCerrar={() => setEditando(false)}
          alListo={(r) => {
            setEditando(false);
            setResultado(r);
          }}
        />
      )}
      {cancelar && (
        <DecidirSolicitud
          empresaId={empresaId}
          solicitud={cancelar}
          accion="cancelar"
          alCerrar={() => setCancelar(null)}
        />
      )}
    </div>
  );
}
